import { track, flush } from '../analytics.js';
import { initPhoneInput } from '../phoneInput.js';
import { regionsForCountry } from '../taxRates.js';
import { withSubmitGuard } from '../submitGuard.js';

const STEPS = 4;

// Industry value → display label. Single source of truth for the Industry dropdown.
const INDUSTRY_OPTIONS = {
  food_beverage:         'Food & Beverage (Restaurant, Café, Food Stall)',
  beauty_wellness:       'Beauty & Wellness (Salon, Spa, Barbershop)',
  health_medical:        'Health & Medical (Clinic, Pharmacy)',
  retail_fashion:        'Retail: Fashion & Apparel',
  retail_electronics:    'Retail: Electronics & Gadgets',
  retail_grocery:        'Retail: Grocery & Convenience',
  retail_home:           'Retail: Home & Furniture',
  professional_services: 'Professional Services (Accounting, Legal)',
  automotive:            'Automotive (Repair, Detailing)',
  education:             'Education & Training',
  entertainment:         'Entertainment & Events',
  technology:            'Technology',
  other:                 'Other',
};

// Which industries are offered for each Business Type. "other" shows the full list
// so a business that doesn't fit a category is never blocked.
const INDUSTRIES_BY_TYPE = {
  food_beverage: ['food_beverage', 'other'],
  service:       ['beauty_wellness', 'health_medical', 'professional_services', 'automotive', 'education', 'entertainment', 'technology', 'other'],
  retail:        ['retail_fashion', 'retail_electronics', 'retail_grocery', 'retail_home', 'other'],
  other:         Object.keys(INDUSTRY_OPTIONS),
};

class OnboardingView {
  _overlay   = document.getElementById('onboardingOverlay');
  _errorEl   = document.getElementById('onboardingError');
  _nextBtn   = document.getElementById('onboardingNextBtn');
  _backBtn   = document.getElementById('onboardingBackBtn');
  _dots      = [...document.querySelectorAll('.ob-dot')];
  _steps     = [...document.querySelectorAll('.ob-step')];
  _current   = 0;
  _startedAt = null;
  _stepTimes = [];
  _phoneIti  = null;

  _onBeforeUnload = () => {
    track('onboarding_abandoned', { last_step: this._current + 1, steps_total: STEPS });
    flush();
  };

  _detectCountry() {
    const tz   = Intl.DateTimeFormat().resolvedOptions().timeZone ?? '';
    const lang = (navigator.language ?? '').toUpperCase();
    if (lang.endsWith('-US') || (tz.startsWith('America/') && !lang.endsWith('-CA') && !lang.endsWith('-MX') && !lang.endsWith('-BR'))) return 'us';
    if (lang.endsWith('-PH') || tz === 'Asia/Manila') return 'ph';
    if (lang.endsWith('-CA') || tz.startsWith('America/Toronto') || tz.startsWith('America/Vancouver') || tz.startsWith('America/Winnipeg')) return 'ca';
    if (lang.endsWith('-GB') || tz === 'Europe/London') return 'gb';
    if (lang.endsWith('-AU') || tz.startsWith('Australia/')) return 'au';
    if (lang.endsWith('-SG') || tz === 'Asia/Singapore') return 'sg';
    if (lang.endsWith('-MY') || tz === 'Asia/Kuala_Lumpur') return 'my';
    if (lang.endsWith('-IN') || tz.startsWith('Asia/Kolkata')) return 'in';
    if (lang.endsWith('-NZ') || tz.startsWith('Pacific/Auckland')) return 'nz';
    if (lang.endsWith('-AE') || tz === 'Asia/Dubai') return 'ae';
    if (lang.endsWith('-JP') || tz === 'Asia/Tokyo') return 'jp';
    return 'us';
  }

  show() {
    this._current   = 0;
    this._startedAt = Date.now();
    this._stepTimes = [Date.now()];
    this._steps.forEach((s, i) => s.classList.toggle('ob-step--hidden', i !== 0));
    this._updateUI();
    this._overlay.classList.remove('hidden');
    this._focusCurrent();

    const detected = this._detectCountry();

    if (!this._phoneIti) {
      this._phoneIti = initPhoneInput('onboardingPhone');
    }
    if (this._phoneIti) this._phoneIti.setCountry(detected);

    const countryEl = document.getElementById('onboardingCountry');
    if (countryEl && !countryEl.value) countryEl.value = detected.toUpperCase();

    // Sync the Industry dropdown to whatever Business Type is currently selected
    // (empty on a fresh open → field stays disabled until a type is picked).
    this._populateIndustries(document.getElementById('onboardingBizType')?.value);

    // Show the right Province/State control (constrained dropdown for US/CA, else text).
    this._syncStateField(countryEl?.value ?? detected.toUpperCase());

    track('onboarding_started');
    track('onboarding_step_viewed', { step: 1 });
    window.addEventListener('beforeunload', this._onBeforeUnload);
  }

  hide() {
    this._overlay.classList.add('hidden');
    window.removeEventListener('beforeunload', this._onBeforeUnload);
  }

  showError(msg) { this._errorEl.textContent = msg; }
  clearError()   { this._errorEl.textContent = ''; }

  setLoading(bool) {
    this._nextBtn.disabled    = bool;
    this._nextBtn.textContent = bool ? 'Saving…' : this._nextLabel();
  }

  _nextLabel() {
    return this._current === STEPS - 1 ? 'Get Started' : 'Next';
  }

  _updateUI() {
    this._dots.forEach((d, i) => d.classList.toggle('ob-dot--active', i === this._current));
    this._backBtn.classList.toggle('hidden', this._current === 0);
    this._nextBtn.textContent = this._nextLabel();
  }

  // Repopulate the Industry dropdown with only the industries valid for `type`.
  // Keeps the current selection if it is still valid; disables the field until a type is chosen.
  _populateIndustries(type) {
    const sel = document.getElementById('onboardingBizIndustry');
    if (!sel) return;
    const values = INDUSTRIES_BY_TYPE[type] ?? [];
    const prev = sel.value;
    sel.innerHTML = '';
    const ph = document.createElement('option');
    ph.value = '';
    ph.disabled = true;
    ph.selected = true;
    ph.textContent = type ? 'Select an industry' : 'Select a business type first';
    sel.appendChild(ph);
    values.forEach((v) => {
      const o = document.createElement('option');
      o.value = v;
      o.textContent = INDUSTRY_OPTIONS[v];
      sel.appendChild(o);
    });
    if (prev && values.includes(prev)) sel.value = prev;
    sel.disabled = values.length === 0;
  }

  // Fill the region dropdown for the given country (e.g. US states, CA provinces).
  // Re-fills when the country changes so US <-> CA switches show the right list.
  _populateRegions(country, regions) {
    const sel = document.getElementById('onboardingStateSelect');
    if (!sel) return;
    const cc = (country ?? '').toUpperCase();
    if (sel.dataset.filledFor === cc) return; // already populated for this country
    sel.innerHTML = '<option value="">Select one</option>';
    regions.forEach(({ code, name }) => {
      const o = document.createElement('option');
      o.value = code;
      o.textContent = name;
      sel.appendChild(o);
    });
    sel.dataset.filledFor = cc;
  }

  // Countries with sub-region tax (US states, CA provinces) get the constrained
  // dropdown so the region can't be mistyped; any other country keeps free text.
  _syncStateField(country) {
    const input = document.getElementById('onboardingProvince');
    const sel   = document.getElementById('onboardingStateSelect');
    if (!input || !sel) return;
    const regions = regionsForCountry(country);
    if (regions) this._populateRegions(country, regions);
    sel.classList.toggle('hidden', !regions);
    input.classList.toggle('hidden', !!regions);
  }

  // Default the phone's country from the address country the user chose in step 3.
  // "OTHER" / a country the phone widget doesn't recognize is left as-is.
  _syncPhoneCountryFromAddress() {
    if (!this._phoneIti) return;
    const cc = document.getElementById('onboardingCountry')?.value;
    if (cc && cc !== 'OTHER') this._phoneIti.setCountry(cc.toLowerCase());
  }

  // The Province / State value, reading whichever control is active for the country.
  _provinceValue() {
    const sel = document.getElementById('onboardingStateSelect');
    if (sel && !sel.classList.contains('hidden')) {
      // Empty value = placeholder still selected -> treat as no input (don't return the
      // placeholder label). Otherwise store the full region name (readable on receipts/
      // address); the tax lookup resolves the name back to a code.
      if (!sel.value) return '';
      return sel.selectedOptions[0]?.textContent.trim() ?? '';
    }
    return document.getElementById('onboardingProvince')?.value.trim() ?? '';
  }

  _focusCurrent() {
    const input = this._steps[this._current]?.querySelector('input, select');
    if (input) setTimeout(() => input.focus(), 200);
  }

  _goTo(index, direction) {
    const timeOnStep = Date.now() - (this._stepTimes[this._current] ?? Date.now());
    if (direction === 'forward') {
      track('onboarding_step_completed', { step: this._current + 1, time_ms: timeOnStep });
    } else {
      track('onboarding_step_back', { from_step: this._current + 1 });
    }

    const outEl = this._steps[this._current];
    const inEl  = this._steps[index];
    const outX  = direction === 'forward' ? '-28px' : '28px';
    const inX   = direction === 'forward' ? '28px'  : '-28px';

    outEl.style.transition = 'opacity 160ms ease, transform 160ms ease';
    outEl.style.opacity    = '0';
    outEl.style.transform  = `translateX(${outX})`;

    setTimeout(() => {
      outEl.classList.add('ob-step--hidden');
      outEl.style.cssText = '';
      inEl.style.opacity   = '0';
      inEl.style.transform = `translateX(${inX})`;
      inEl.classList.remove('ob-step--hidden');
      requestAnimationFrame(() => requestAnimationFrame(() => {
        inEl.style.transition = 'opacity 160ms ease, transform 160ms ease';
        inEl.style.opacity    = '1';
        inEl.style.transform  = 'translateX(0)';
        setTimeout(() => { inEl.style.cssText = ''; }, 170);
      }));
      this._current = index;
      this._stepTimes[index] = Date.now();
      this._updateUI();
      this.clearError();
      this._focusCurrent();
      // Reaching the phone step (index 3): seed its country from the address country.
      if (index === 3) this._syncPhoneCountryFromAddress();
      track('onboarding_step_viewed', { step: index + 1 });
    }, 160);
  }

  _validate() {
    if (this._current === 0) {
      if (!document.getElementById('onboardingBizName')?.value.trim()) {
        this.showError('Please enter your business name.');
        return false;
      }
    }
    if (this._current === 1) {
      if (!document.getElementById('onboardingBizType')?.value) {
        this.showError('Please select a business type.');
        return false;
      }
      if (!document.getElementById('onboardingBizIndustry')?.value) {
        this.showError('Please select an industry.');
        return false;
      }
    }
    // Step 3 (index 2) is now Address, Step 4 (index 3) is Phone - address is collected
    // first so the phone's country can default from the chosen address country.
    if (this._current === 2) {
      const street   = document.getElementById('onboardingStreet')?.value.trim();
      const city     = document.getElementById('onboardingCity')?.value.trim();
      const province = this._provinceValue();
      const zip      = document.getElementById('onboardingZip')?.value.trim();
      const country  = document.getElementById('onboardingCountry')?.value;
      if (!country)  { this.showError('Please select your country.'); return false; }
      if (!street)   { this.showError('Please enter your street address.'); return false; }
      if (!city)     { this.showError('Please enter your city or municipality.'); return false; }
      if (!province) { this.showError('Please enter your province or state.'); return false; }
      if (!zip)      { this.showError('Please enter your ZIP or postal code.'); return false; }
    }
    if (this._current === 3) {
      const phone = this._phoneIti?.getNumber() ?? '';
      if (!phone) { this.showError('Please enter a phone number.'); return false; }
      if (!this._phoneIti?.isValidNumber()) { this.showError('Please enter a valid phone number.'); return false; }
    }
    return true;
  }

  _addHandlerExit(handler) {
    document.getElementById('onboardingExitLink')?.addEventListener('click', (e) => {
      e.preventDefault();
      handler();
    });
  }

  _addHandlerSubmit(handler) {
    this._nextBtn.addEventListener('click', () => {
      this.clearError();
      if (!this._validate()) return;
      if (this._current < STEPS - 1) {
        this._goTo(this._current + 1, 'forward');
      } else {
        const totalMs = Date.now() - (this._startedAt ?? Date.now());
        track('onboarding_completed', { total_time_ms: totalMs });
        withSubmitGuard(this._nextBtn, () => handler({
          businessName: document.getElementById('onboardingBizName')?.value.trim(),
          businessType: document.getElementById('onboardingBizType')?.value,
          industry:     document.getElementById('onboardingBizIndustry')?.value,
          phone:        this._phoneIti?.getNumber() ?? '',
          street:       document.getElementById('onboardingStreet')?.value.trim(),
          city:         document.getElementById('onboardingCity')?.value.trim(),
          province:     this._provinceValue(),
          zip:          document.getElementById('onboardingZip')?.value.trim(),
          country:      document.getElementById('onboardingCountry')?.value,
        })); // text/disable owned by the controller's setLoading(); guard adds re-entry protection
      }
    });

    this._backBtn.addEventListener('click', () => {
      if (this._current > 0) this._goTo(this._current - 1, 'back');
    });

    // Industry options depend on the chosen Business Type - repopulate on change.
    document.getElementById('onboardingBizType')?.addEventListener('change', (e) => {
      this._populateIndustries(e.target.value);
    });

    // Country drives whether Province/State is a constrained US dropdown or free text.
    document.getElementById('onboardingCountry')?.addEventListener('change', (e) => {
      this._syncStateField(e.target.value);
    });

    this._overlay.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && !this._nextBtn.disabled) {
        e.preventDefault();
        this._nextBtn.click();
      }
    });
  }

  // --- Tax suggestion prompt (shown after onboarding when a rate is known) ---

  showTaxPrompt({ label, rate }) {
    const overlay = document.getElementById('taxSuggestOverlay');
    const promptEl = document.getElementById('taxSuggestPrompt');
    if (!overlay || !promptEl) return;
    promptEl.textContent = `Add ${label} (${rate}%) to your receipts?`;
    overlay.classList.remove('hidden');
  }

  hideTaxPrompt() {
    document.getElementById('taxSuggestOverlay')?.classList.add('hidden');
  }

  setTaxLoading(bool) {
    const addBtn = document.getElementById('taxSuggestAddBtn');
    if (addBtn) {
      addBtn.disabled = bool;
      addBtn.textContent = bool ? 'Adding…' : 'Add to receipts';
    }
  }

  _addHandlerTaxPrompt(onAdd, onSkip) {
    document.getElementById('taxSuggestAddBtn')?.addEventListener('click', () => onAdd());
    document.getElementById('taxSuggestSkipBtn')?.addEventListener('click', () => onSkip());
  }
}

export default new OnboardingView();
